import { useErrorInlineNotification } from '@/components/ErrorInlineNotification';
import { ApprovingInvoiceReportsSearchQuery } from '@/context/services/approving_invoice_reports';
import {
  useBoundingClientRect,
  useWindowResize,
} from '@moneyforward/ap-frontend-components';
import { ApReportOrderByDirection, ApReportOrderByField } from 'ap-openapi';
import classnames from 'classnames/bind';
import { FC, memo, useCallback, useMemo, useRef } from 'react';
import { Search } from '../Search';
import { PictureInPictureModeProvider } from './context/PictureInPictureModeProvider';
import {
  mapColumnKeyToApiSortField,
  useApprovingInvoiceReportsFormInitial,
  useApprovingInvoiceReportsHandleSearchQuery,
} from './hooks';
import styles from './List.module.scss';
import { useApprovingInvoiceReportsSearchData } from './Table/hook';
import { ApprovingInvoiceReportsTableView } from './Table/Table';
const cx = classnames.bind(styles);

export const ApprovingInvoiceReports: FC = memo(() => {
  const defaultValues = useApprovingInvoiceReportsFormInitial();
  const { searchQuery, onSearch, handleMultiColumnSort, sortOrders } =
    useApprovingInvoiceReportsHandleSearchQuery(defaultValues);

  // Dynamic height calculation for container
  const containerRef = useRef<HTMLDivElement>(null);
  const { innerHeight } = useWindowResize();
  const { top } = useBoundingClientRect(containerRef);
  const containerStyle = useMemo(
    () => ({
      height: top > 0 ? innerHeight - top : innerHeight,
    }),
    [innerHeight, top]
  );

  // Get refetch function from Table hook
  const tableQuery = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { page, sort_fields, sort_orders, ...searchParams } = searchQuery;

    // Convert all sortOrders to order_by array format for API (multi-column sort)
    // Map each sort state from UI to API format with proper field mapping
    const order_by:
      | Array<{
          field: ApReportOrderByField;
          direction: ApReportOrderByDirection;
        }>
      | undefined =
      sortOrders && sortOrders.length > 0
        ? sortOrders.map((sortState) => {
            const direction: 'asc' | 'desc' =
              sortState.order === 'ascend' ? 'asc' : 'desc';
            return {
              field: mapColumnKeyToApiSortField(sortState.columnKey),
              direction,
            };
          })
        : undefined;

    return {
      query: { ...searchParams },
      page,
      order_by,
    };
  }, [searchQuery, sortOrders]);

  const [
    { setError },
    ErrorContextHolder,
    ErrorInlineNotificationProvider,
    onClearMessage,
  ] = useErrorInlineNotification();

  const { data, pagination, refetch, isFetching } =
    useApprovingInvoiceReportsSearchData(tableQuery, setError);

  const onHandleSearch = useCallback(
    (data: ApprovingInvoiceReportsSearchQuery) => {
      onClearMessage();
      onSearch(data, refetch);
    },
    [onSearch, onClearMessage, refetch]
  );

  return (
    <PictureInPictureModeProvider>
      <div
        className={cx(styles['container'])}
        ref={containerRef}
        style={containerStyle}
      >
        <ErrorInlineNotificationProvider value={setError}>
          <div className={cx(styles['inline-notification-wrap'])}>
            {ErrorContextHolder}
          </div>
          <Search
            defaultValues={defaultValues}
            onSearch={onHandleSearch}
            searchQuery={searchQuery}
            isLoading={isFetching}
          />
          <ApprovingInvoiceReportsTableView
            searchQuery={searchQuery}
            onSearch={onHandleSearch}
            onMultiColumnSort={handleMultiColumnSort}
            sortOrders={sortOrders}
            query={tableQuery}
            data={data}
            pagination={pagination}
            isFetching={isFetching || false}
            refetch={refetch}
          />
        </ErrorInlineNotificationProvider>
      </div>
    </PictureInPictureModeProvider>
  );
});

ApprovingInvoiceReports.displayName = 'ApprovingInvoiceReports';
